import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import type { SessionUser } from "@devengo/shared";

import { ApiError, api } from "./api";
import type { TenantBranding } from "./branding";

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await api.get<SessionUser>("/api/auth/session");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useCurrentUser(): SessionUser {
  const { data } = useSession();
  if (!data) throw new Error("useCurrentUser fuera de una sesión.");
  return data;
}

/**
 * Ends the session and shows the login. The session is set to "none" rather than invalidated:
 * clearing the cache first left nothing to refetch, and the root kept the old user on screen.
 * A 401 means the session had already ended, so this browser logs out too; any other failure is
 * thrown, because the cookie would still be valid and a reload would log back in.
 */
export function useLogout() {
  const client = useQueryClient();
  return async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) throw error;
    }
    client.setQueryData(["session"], null);
    // The previous user's data must not survive into the next login.
    client.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
  };
}

export const BrandingContext = createContext<TenantBranding | null>(null);

export function useBranding(): TenantBranding {
  const branding = useContext(BrandingContext);
  if (!branding) throw new Error("El branding no se cargó antes del render.");
  return branding;
}

export const ROLE_LABELS = { dueno: "Dueño", contador: "Contador", operador: "Operador" } as const;

/** The month the VAT cycle usually works on: the one that just ended. */
export function defaultPeriod(today = new Date()): string {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function parsePeriod(value: string | undefined): { year: number; month: number } {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? "");
  if (match) return { year: Number(match[1]), month: Number(match[2]) };
  return parsePeriod(defaultPeriod());
}

export function periodParam(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}
